import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { fetchFormDetails, updateForm } from "../utils/api"

export default function FormEdit() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [form, setForm] = useState({ name: "", enable: false })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!id) return
    const load = async () => {
      try {
        const data = await fetchFormDetails(id)
        setForm({ name: data.form.name, enable: data.form.enable })
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await updateForm(id, form)
      navigate("/form-list")
    } catch (err) {
      console.error(err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/form-list")}
            className="mb-4 text-slate-600 hover:text-slate-900 flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Forms
          </button>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Edit Form</h1>
          <p className="text-slate-600">Update your form settings and configuration</p>
        </div>

        {/* Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-xl border border-white/20 overflow-hidden">
          <div className="px-6 py-6 border-b border-slate-100">
            <h2 className="text-xl font-semibold text-slate-900 mb-1">Form Configuration</h2>
            <p className="text-slate-600 text-sm">Modify the form name and toggle its active status</p>
          </div>

          <div className="px-6 py-6">
            {error && <p className="text-red-600 mb-4">Error: {error}</p>}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                  Form Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Enter form name..."
                  required
                  className="w-full h-11 px-3 py-2 border border-slate-200 rounded-md shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 transition-colors"
                />
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center h-5">
                  <input
                    id="enable"
                    type="checkbox"
                    checked={form.enable}
                    onChange={(e) => setForm((f) => ({ ...f, enable: e.target.checked }))}
                    className="w-4 h-4 text-slate-900 bg-white border-slate-300 rounded focus:ring-slate-400 focus:ring-2"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="enable" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Active Status
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    {form.enable
                      ? "Form is currently active and accepting responses"
                      : "Form is inactive and not accepting responses"}
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-8 py-3 rounded-md font-medium transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">Changes will be saved immediately and take effect right away</p>
        </div>
      </div>
    </div>
  )
}
